import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import StraightenRounded from '@mui/icons-material/StraightenRounded';
import VerifiedUserRounded from '@mui/icons-material/VerifiedUserRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AttachmentsPanel } from '../attachments';
import { ApiError, api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { dateTime, money, qty } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { Field, StatCard } from '../contacts/ui';
import { ImagesPanel } from './ImagesPanel';
import {
  catalogKeys,
  conversionLabel,
  marginPercent,
  packBreakdown,
  priceRange,
  trimDecimal,
  type Product,
  type Variant,
} from './model';
import { ProductEditDialog, UnitsDialog, VariantDialog } from './ProductDialogs';
import { useProduct } from './queries';
import { BomPanel } from './BomPanel';
import { ComboPanel } from './ComboPanel';
import { ActiveChip, ProductThumb } from './ui';
import { MoreMenu } from '../../components/MoreMenu';

type TabKey = 'variants' | 'combo' | 'recipe' | 'units' | 'photos' | 'details';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'variants', label: 'Variants' },
  { key: 'combo', label: 'Combo choices' },
  { key: 'recipe', label: 'Bill of materials' },
  { key: 'units', label: 'Pack sizes' },
  { key: 'photos', label: 'Photos' },
  { key: 'details', label: 'Details' },
];

/** /products/:id — header, summary cards and tabs for variants, pack sizes, photos and details. */
export function ProductDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const data = useProduct(id);
  const p = data.data;
  const [editing, setEditing] = useState(false);
  const tabParam = params.get('tab') as TabKey | null;
  // Only goods are made from components; only combos have choices.
  const tabs = TABS.filter(
    (t) =>
      (t.key !== 'recipe' || p?.productType === 'GOODS') &&
      (t.key !== 'combo' || p?.productType === 'COMBO'),
  );
  const tab: TabKey = tabs.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'variants';

  if (data.error) {
    const missing = data.error instanceof ApiError && data.error.status === 404;
    return (
      <Card>
        <EmptyState
          icon={<CategoryRounded />}
          title={missing ? 'This product doesn’t exist' : 'Couldn’t load this product'}
          body={missing ? 'It may have been deleted, or the link is wrong.' : data.error.message}
          action={{ label: 'Back to products', onClick: () => void navigate('/products') }}
        />
      </Card>
    );
  }

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBackRounded />}
        onClick={() => void navigate('/products')}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        Products
      </Button>

      <Header product={p} onEdit={() => setEditing(true)} />

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          my: 3,
        }}
      >
        <SummaryCards product={p} />
      </Box>

      <Card sx={{ mb: 2.5, px: { xs: 1, sm: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => {
            const next = new URLSearchParams(params);
            if (v === 'variants') next.delete('tab');
            else next.set('tab', v);
            setParams(next);
          }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={
                p && t.key !== 'details' && t.key !== 'recipe' && t.key !== 'combo'
                  ? `${t.label} · ${String(t.key === 'variants' ? p.variants.length : t.key === 'units' ? p.units.length : p.images.length)}`
                  : t.label
              }
            />
          ))}
        </Tabs>
      </Card>

      {!p ? (
        <Card>
          <CardContent>
            <Skeleton height={36} width="40%" />
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} width="80%" />
          </CardContent>
        </Card>
      ) : tab === 'variants' ? (
        <VariantsPanel product={p} />
      ) : tab === 'combo' ? (
        <ComboPanel key={p.version} product={p} />
      ) : tab === 'recipe' ? (
        <BomPanel product={p} />
      ) : tab === 'units' ? (
        <UnitsPanel product={p} />
      ) : tab === 'photos' ? (
        <ImagesPanel product={p} />
      ) : (
        <>
          <DetailsPanel product={p} />
          <AttachmentsPanel recordType="PRODUCT" recordId={id} sx={{ mt: 3 }} />
        </>
      )}

      {p && <ProductEditDialog open={editing} product={p} onClose={() => setEditing(false)} />}
    </>
  );
}

function Header({ product: p, onEdit }: { product: Product | undefined; onEdit: () => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<'delete' | 'deactivate' | null>(null);

  const toggleActive = useMutation({
    mutationFn: (product: Product) =>
      unwrap(
        api.PATCH('/products/{id}', {
          params: { path: { id: product.id } },
          body: { isActive: !product.isActive, version: product.version },
        }),
      ),
    onSuccess: async (saved) => {
      notify.success(saved.isActive ? `${saved.name} reactivated` : `${saved.name} deactivated`);
      qc.setQueryData(catalogKeys.product(saved.id), saved);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
    },
    onError: (e) => notify.error(e),
  });

  if (!p)
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Skeleton variant="rounded" width={72} height={72} sx={{ borderRadius: '18px' }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width={280} height={38} />
          <Skeleton width={180} />
        </Box>
      </Stack>
    );

  const canUpdate = can('products.update');
  const canDelete = can('products.delete');
  const cover = [...p.images].sort((a, b) => a.sortOrder - b.sortOrder)[0];

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2.5}
        sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
          <ProductThumb productId={p.id} imageId={cover?.id} size={72} radius={18} />
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1.25}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.75 }}
            >
              <Typography variant="h3" sx={{ wordBreak: 'break-word' }}>
                {p.name}
              </Typography>
              <ActiveChip active={p.isActive} />
              <Chip size="small" variant="outlined" label={typeLabel(p)} />
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 0.75,
                flexWrap: 'wrap',
                rowGap: 0.5,
                color: 'text.secondary',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                {p.code}
              </Typography>
              {[p.category?.path, p.brand?.name].filter(Boolean).map((t) => (
                <Typography key={t} variant="body2">
                  · {t}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.25} sx={{ flexShrink: 0 }}>
          {canUpdate && (
            <Button variant="contained" startIcon={<EditRounded />} onClick={onEdit}>
              Edit
            </Button>
          )}
          <MoreMenu
            onDuplicate={
              can('products.create')
                ? () => void navigate('/products/new', { state: { copy: p } })
                : undefined
            }
            exportModel="product"
            exportId={p.id}
            actions={[
              ...(canUpdate
                ? [
                    {
                      key: 'active',
                      label: p.isActive ? 'Deactivate' : 'Reactivate',
                      icon: <BlockRounded fontSize="small" />,
                      onClick: () => {
                        if (p.isActive) setConfirm('deactivate');
                        else toggleActive.mutate(p);
                      },
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteOutlineRounded fontSize="small" />,
                      onClick: () => setConfirm('delete'),
                      danger: true,
                    },
                  ]
                : []),
            ]}
          />
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirm === 'deactivate'}
        title={`Deactivate ${p.name}?`}
        body="It disappears from search and pickers on new documents. Stock, history and reports stay as they are, and you can reactivate it any time."
        confirmLabel="Deactivate"
        onConfirm={() => toggleActive.mutateAsync(p).catch(() => undefined)}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        danger
        title={`Delete ${p.name}?`}
        body={`This removes the product and its ${String(p.variants.length)} variant${p.variants.length === 1 ? '' : 's'} from the catalog. Items already used on documents or holding stock can’t be deleted — deactivate them instead.`}
        confirmLabel="Delete"
        onConfirm={() =>
          unwrap(
            api.DELETE('/products/{id}', {
              params: { path: { id: p.id }, query: { version: p.version } },
            }),
          )
            .then(async () => {
              notify.success(`${p.name} deleted`);
              qc.removeQueries({ queryKey: catalogKeys.product(p.id) });
              await qc.invalidateQueries({ queryKey: catalogKeys.products });
              void navigate('/products');
            })
            .catch((e: unknown) => notify.error(e))
        }
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

function SummaryCards({ product: p }: { product: Product | undefined }) {
  const { format } = useShop();
  const loading = !p;
  const range = p ? priceRange(p.variants) : null;
  const active = p?.variants.filter((v) => v.isActive).length ?? 0;
  const biggest = p?.units.reduce<Product['units'][number] | undefined>(
    (best, u) => (!best || Number(u.factor) > Number(best.factor) ? u : best),
    undefined,
  );
  return (
    <>
      <StatCard
        label="Sale price"
        icon={<SellRounded />}
        loading={loading}
        value={
          range
            ? range.min === range.max
              ? money(range.min, format)
              : `${money(range.min, format)}–${money(range.max, format)}`
            : '—'
        }
        hint={
          p
            ? `per ${p.baseUnit.symbol}${range && range.min !== range.max ? ' · range across variants' : ''}`
            : ''
        }
      />
      <StatCard
        label="Variants"
        icon={<LayersRounded />}
        loading={loading}
        value={String(active)}
        tone={p && active === 0 ? 'warning' : 'default'}
        hint={
          p
            ? p.variants.length > active
              ? `${String(p.variants.length - active)} inactive`
              : 'All active'
            : ''
        }
      />
      <StatCard
        label="Stock unit"
        icon={<StraightenRounded />}
        loading={loading}
        value={p ? p.baseUnit.name : ''}
        hint={
          p
            ? biggest
              ? conversionLabel(biggest, biggest.factor, p.baseUnit)
              : p.baseUnit.allowsFraction
                ? 'Fractions allowed'
                : 'Whole numbers only'
            : ''
        }
      />
      <StatCard
        label={p?.stock ? 'On hand' : 'Tracking'}
        icon={p?.trackSerial ? <VerifiedUserRounded /> : <Inventory2Rounded />}
        loading={loading}
        tone={
          p && !p.trackInventory
            ? 'info'
            : p?.stock && Number(p.stock.onHand) <= 0
              ? 'warning'
              : 'default'
        }
        value={
          p
            ? p.stock
              ? `${qty(p.stock.onHand, format)} ${p.baseUnit.symbol}`
              : p.trackInventory
                ? 'Stock tracked'
                : 'No stock'
            : ''
        }
        hint={
          p
            ? [
                p.stock ? packBreakdown(p.stock.onHand, p.baseUnit, p.units) : null,
                p.stock && p.stock.available !== p.stock.onHand
                  ? `${qty(p.stock.available, format)} available`
                  : null,
                p.trackSerial ? 'Serial numbers' : null,
                p.warrantyMonths ? `${String(p.warrantyMonths)}-month warranty` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'No serials or warranty'
            : ''
        }
      />
    </>
  );
}

function VariantsPanel({ product: p }: { product: Product }) {
  const { can } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Variant | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Variant | null>(null);
  const canUpdate = can('products.update');
  const canDelete = can('products.delete');
  const canViewCost = can('products.view_cost');
  const { format } = useShop();

  const columns: Column<Variant>[] = [
    {
      key: 'name',
      header: 'Variant',
      render: (v) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {v.nameSuffix ?? v.displayName}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
            {v.attributes.map((a) => (
              <Chip
                key={a.definitionId}
                size="small"
                label={`${a.name}: ${a.value}${a.unitHint ? ` ${a.unitHint}` : ''}`}
                sx={{ height: 20, fontSize: 11 }}
              />
            ))}
          </Stack>
        </Box>
      ),
    },
    {
      key: 'sku',
      header: 'SKU / barcode',
      hideBelow: 'sm',
      render: (v) => (
        <Box sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8125rem' }}>
          <div>{v.sku}</div>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
            {v.barcode ?? 'No barcode'}
          </Typography>
        </Box>
      ),
    },
    ...(canViewCost
      ? [
          {
            key: 'cost',
            header: 'Cost',
            align: 'right' as const,
            hideBelow: 'md' as const,
            render: (v: Variant) => {
              const m = marginPercent(v.purchasePrice, v.salePrice);
              return (
                <Box>
                  <Money value={v.purchasePrice} />
                  {m !== null && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: m < 0 ? 'error.main' : m < 10 ? 'warning.main' : 'success.main',
                        fontWeight: 600,
                      }}
                    >
                      {m.toFixed(1)}% margin
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
    ...(p.stock
      ? [
          {
            key: 'onHand',
            header: 'On hand',
            align: 'right' as const,
            render: (v: Variant) => {
              const row = p.stock?.variants.find((x) => x.variantId === v.id);
              const n = row?.onHand ?? '0';
              return (
                <Box>
                  <Typography
                    variant="body2"
                    className="tabular"
                    sx={{ fontWeight: 600, color: Number(n) <= 0 ? 'error.main' : undefined }}
                  >
                    {qty(n, format)} {p.baseUnit.symbol}
                  </Typography>
                  {packBreakdown(n, p.baseUnit, p.units) && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {packBreakdown(n, p.baseUnit, p.units)}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
    {
      key: 'low',
      header: 'Low-stock at',
      align: 'right',
      hideBelow: 'lg',
      render: (v) =>
        v.minStockLevel === null ? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ) : (
          <span className="tabular">
            {trimDecimal(v.minStockLevel)} {p.baseUnit.symbol}
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'md',
      render: (v) => <ActiveChip active={v.isActive} />,
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (v) => <Money value={v.salePrice} strong />,
    },
    ...(canUpdate || canDelete
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            width: 96,
            render: (v: Variant) => (
              <Stack
                direction="row"
                spacing={0.25}
                sx={{ justifyContent: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
              >
                {canUpdate && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      aria-label={`Edit ${v.displayName}`}
                      onClick={() => setEditing(v)}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip
                    title={
                      p.variants.length === 1 ? 'A product needs at least one variant' : 'Delete'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${v.displayName}`}
                        disabled={p.variants.length === 1}
                        onClick={() => setDeleting(v)}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={2}>
      <DataTable
        columns={columns}
        rows={p.variants}
        rowKey={(v) => v.id}
        onRowClick={canUpdate ? (v) => setEditing(v) : undefined}
        toolbar={
          <>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Prices are per {p.baseUnit.name.toLowerCase()} ({p.baseUnit.symbol}).
            </Typography>
            {canUpdate && (
              <Button
                startIcon={<AddRounded />}
                variant="outlined"
                onClick={() => setEditing('new')}
              >
                Add variant
              </Button>
            )}
          </>
        }
      />
      <VariantDialog
        open={editing !== null}
        product={p}
        variant={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!deleting}
        danger
        title={`Delete ${deleting?.displayName ?? 'variant'}?`}
        body="Variants already used on documents or holding stock can’t be deleted — deactivate them instead."
        confirmLabel="Delete"
        onConfirm={() => {
          const v = deleting;
          if (!v) return Promise.resolve();
          return unwrap(
            api.DELETE('/variants/{id}', {
              params: { path: { id: v.id }, query: { version: v.version } },
            }),
          )
            .then(async () => {
              notify.success(`${v.displayName} deleted`);
              await qc.invalidateQueries({ queryKey: catalogKeys.products });
            })
            .catch((e: unknown) => notify.error(e));
        }}
        onClose={() => setDeleting(null)}
      />
    </Stack>
  );
}

function UnitsPanel({ product: p }: { product: Product }) {
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const canUpdate = can('products.update');
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 2.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1.0625rem' }}>
              Stock is counted in {p.baseUnit.name.toLowerCase()} ({p.baseUnit.symbol})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pack sizes let you buy and sell in bigger units; quantities convert to{' '}
              {p.baseUnit.symbol}.
            </Typography>
          </Box>
          {canUpdate && (
            <Button variant="outlined" startIcon={<EditRounded />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </Stack>
        {p.units.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              borderRadius: '14px',
              border: 1,
              borderStyle: 'dashed',
              borderColor: 'divider',
            }}
          >
            <Typography color="text.secondary">
              No pack sizes — sold and bought by the {p.baseUnit.symbol} only.
            </Typography>
            {canUpdate && (
              <Button startIcon={<AddRounded />} sx={{ mt: 1.5 }} onClick={() => setEditing(true)}>
                Add pack size
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
            }}
          >
            {p.units.map((u) => (
              <Box
                key={u.unitId}
                sx={(t) => {
                  const c = (t.vars ?? t).palette.primary.main;
                  return {
                    p: 2.25,
                    borderRadius: '14px',
                    border: 1,
                    borderColor: 'divider',
                    background: `linear-gradient(135deg, color-mix(in srgb, ${c} 6%, transparent), transparent 70%)`,
                  };
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  {u.name}
                </Typography>
                <Typography variant="h5" className="tabular">
                  {conversionLabel(u, u.factor, p.baseUnit)}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                  {u.isDefaultPurchase && (
                    <Chip size="small" color="info" variant="outlined" label="Default for buying" />
                  )}
                  {u.isDefaultSale && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label="Default for selling"
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
      <UnitsDialog open={editing} product={p} onClose={() => setEditing(false)} />
    </Card>
  );
}

function DetailsPanel({ product: p }: { product: Product }) {
  const { format } = useShop();
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
          }}
        >
          <Field label="Product code">
            <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>
              {p.code}
            </Box>
          </Field>
          <Field label="Category">{p.category?.path ?? '—'}</Field>
          <Field label="Brand">{p.brand?.name ?? '—'}</Field>
          <Field label="Manufacturer">{p.manufacturer?.name ?? '—'}</Field>
          <Field label="Base unit">
            {p.baseUnit.name} ({p.baseUnit.symbol}) ·{' '}
            {p.baseUnit.allowsFraction ? 'fractions allowed' : 'whole numbers'}
          </Field>
          <Field label="Warranty">
            {p.warrantyMonths ? `${String(p.warrantyMonths)} months` : 'None'}
          </Field>
          <Field label="Created">{dateTime(p.createdAt, format)}</Field>
          <Field label="Last changed">{dateTime(p.updatedAt, format)}</Field>
        </Box>
        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Field label="Description">
            <Box component="span" sx={{ whiteSpace: 'pre-line', fontWeight: 400 }}>
              {p.description ?? 'No description yet.'}
            </Box>
          </Field>
        </Box>
      </CardContent>
    </Card>
  );
}

function typeLabel(p: Product) {
  if (p.productType === 'SERVICE') return 'Service';
  if (p.productType === 'COMBO') return 'Combo';
  const bom = p.variants.some((v) => v.recipe);
  if (p.trackInventory) return bom ? 'Goods · made from components' : 'Goods';
  return bom ? 'Goods · components taken on sale' : 'Goods · no stock';
}
